"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircleIcon, RefreshCwIcon } from "lucide-react";
import { useAppEvent } from "@/lib/events";
import { useAuth } from "@/lib/auth/AuthContext";
import { cn } from "@/lib/utils";

/**
 * Persistent top banner shown when the backend's IDP circuit breaker reports
 * the identity provider is down (503 + X-IDP-Unavailable: 1). Stays visible
 * until either:
 *   - an `idp-restored` event fires, OR
 *   - the user clicks Retry and the resulting whoami probe succeeds.
 *
 * The banner does NOT auto-poll — bandwidth and breaker fairness matter more
 * than perceived recovery speed; users can retry on demand.
 */
export function IdpUnavailableBanner() {
  const [visible, setVisible] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const { t } = useTranslation();
  const auth = useAuth();

  useAppEvent("idp-unavailable", () => setVisible(true));
  useAppEvent("idp-restored", () => setVisible(false));

  if (!visible) return null;

  async function handleRetry() {
    setRetrying(true);
    try {
      const next = await auth.refresh();
      if (next === "authed") setVisible(false);
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div
      role="alert"
      className={cn(
        "fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-3 px-4 py-2",
        "bg-destructive/95 text-destructive-foreground text-sm font-medium shadow-md",
      )}
    >
      <AlertCircleIcon className="size-4 shrink-0" />
      <div className="flex flex-col items-start sm:flex-row sm:items-center sm:gap-2">
        <span className="font-semibold">{t("idp.bannerTitle")}</span>
        <span className="opacity-90">{t("idp.bannerDescription")}</span>
      </div>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retrying}
        className={cn(
          "ml-2 inline-flex items-center gap-1.5 rounded-md border border-destructive-foreground/40",
          "px-2.5 py-1 text-xs hover:bg-destructive-foreground/10 disabled:opacity-60",
        )}
      >
        <RefreshCwIcon className={cn("size-3.5", retrying && "animate-spin")} />
        {t("idp.retry")}
      </button>
    </div>
  );
}
